import React, { PropsWithChildren } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DropdownMenu as RadixDropdownMenu } from "radix-ui";
import { ChevronDown, ExternalLink } from "lucide-react";

import { Icon } from "@/design-system/components/Icon/Icon";
import { ConditionalTooltip } from "../Tooltip/Tooltip";

type DropdownMenuProps<T> = PropsWithChildren<{
  options: T[];
  getOptionLabel: (opt: T) => string;
  getIconBefore?: (opt: T) => React.ReactNode | undefined;
  getIconAfter?: (opt: T) => boolean | undefined;
  getOptionUrl?: (opt: T) => string | undefined;
  getIsDestructive?: (opt: T) => boolean | undefined;
  onSelectOption?: (opt: T) => void;
  icon?: React.ReactNode;
  tooltipContent?: React.ReactNode;
  contentProps?: React.ComponentProps<typeof RadixDropdownMenu.Content>;
  rootProps?: React.ComponentProps<typeof RadixDropdownMenu.Root>;
  itemProps?: React.ComponentProps<typeof RadixDropdownMenu.Item>;
  triggerProps?: React.ComponentProps<typeof RadixDropdownMenu.Trigger>;
}>;

export function DropdownMenu<T>({
  children,
  options,
  getOptionLabel,
  getIconBefore,
  getIconAfter,
  getOptionUrl,
  getIsDestructive,
  onSelectOption,
  icon = <ChevronDown />,
  tooltipContent,
  contentProps,
  rootProps,
  itemProps,
  triggerProps,
}: DropdownMenuProps<T>) {
  return (
    <RadixDropdownMenu.Root {...rootProps}>
      <ConditionalTooltip condition={!!tooltipContent} content={tooltipContent}>
        <RadixDropdownMenu.Trigger asChild>
          <button
            {...triggerProps}
            className={clsx(
              twMerge(
                "hover:bg-surface-selected h-12 w-12 rounded-lg p-2 hover:cursor-pointer",
                triggerProps?.className,
              ),
            )}
            aria-label={triggerProps?.["aria-label"] ?? "Dropdown menu"}
          >
            {icon}
          </button>
        </RadixDropdownMenu.Trigger>
      </ConditionalTooltip>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          {...contentProps}
          className={clsx(
            twMerge(
              "bg-surface-default z-popover shadow-elevation-200 border-interactive-selected relative rounded-lg border",
              contentProps?.className,
            ),
          )}
        >
          {children ??
            options.map((option: T, index: number) => {
              const iconBefore = getIconBefore?.(option);
              const iconAfter = getIconAfter?.(option);
              const optionUrl = getOptionUrl?.(option);
              const isDestructive = getIsDestructive?.(option);

              return (
                <RadixDropdownMenu.Item
                  {...itemProps}
                  className={clsx(
                    twMerge(
                      "hover:bg-surface-selected focus:bg-surface-selected flex items-center justify-between px-4 py-3 first-of-type:rounded-t-md last-of-type:rounded-b-md hover:cursor-pointer hover:!border-none hover:outline-0 focus:outline-0",
                      itemProps?.className,
                      isDestructive &&
                        "text-textError hover:bg-status-error-tint focus:bg-status-error-tint",
                    ),
                  )}
                  key={index}
                  onSelect={() => onSelectOption?.(option)}
                >
                  <div className="flex items-center gap-2">
                    {iconBefore && (
                      <span className="[&>svg]:!text-iconDefault flex-shrink-0">
                        {iconBefore}
                      </span>
                    )}
                    {optionUrl ? (
                      <a
                        href={optionUrl}
                        {...(iconAfter && {
                          target: "_blank",
                          rel: "noopener noreferrer",
                        })}
                        className="text-textDefault block no-underline"
                      >
                        {getOptionLabel(option)}
                      </a>
                    ) : (
                      <span>{getOptionLabel(option)}</span>
                    )}
                  </div>
                  {iconAfter && (
                    <span className="ml-2 flex-shrink-0">
                      <Icon
                        icon={ExternalLink}
                        size="lg"
                        className="text-iconMinimal"
                      />
                    </span>
                  )}
                </RadixDropdownMenu.Item>
              );
            })}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  );
}
